import { createSlice } from '@reduxjs/toolkit';

/**
 * @private
 * @description Initial state for paging store state for a paged entity.
 */
const pagingInitialState = {
  searchTerm__: [],
};

/**
 * @public
 * @param {string} searchTerm - A search term.
 * @returns Formatted search term as key.
 */
export const toSearchTermKey = (searchTerm) => `searchTerm_${searchTerm}_`;

/**
 * To define the default functionality for request page reducer.
 * @private
 * @param {StoreState} state
 * @param {{payload: any;type: string;}} action
 * @returns New store state
 */
const requestPageDefault = (state, action) => {
  const key = toSearchTermKey(action.payload);
  /**
   * if there is already an index for search term then return the current state.
   * otherwise, init new index for the search term and rehtn the new state.
   */
  return {
    ...state,
    [key]: state[key] ?? [],
  };
};

/**
 * To define the default functionality for add page reducer.
 * @private
 * @param {StoreState} state
 * @param {{payload: any;type: string;}} action
 * @returns New store state.
 */
const addPageDefault = (state, action) => {
  const key = toSearchTermKey(action.payload.searchTerm);
  const { data } = action.payload;
  return {
    ...state,
    [key]: Array.from(new Set([ ...state[key], ...data ])),
  };
};

/**
 * A function that accepts an entity name and automatically generates action creators and
 * action types that correspond to the reducers and state for a paged entity.
 * @public
 * @param {string} entityName - Name of the entity to create pagination slice for.
 * @example
 *  Sample pagination slice structure:
 *  let store = {
 *    pagination : {
 *      machines: {
 *       searchTerm__: [1,2],
 *       searchTerm_JEL_: [1,3,5]
 *      },
 *      edgeFileExports: {
 *        searchTerm_1_: [1,2],
 *      }
 *    }
 * }
 * @returns A pagination slice for the entityName.
 */
export const createPaginationSlice = (
  entityName,
  requestPage = requestPageDefault,
  addPage = addPageDefault
) => createSlice({
  name: entityName,
  initialState: pagingInitialState,
  reducers: {
    requestPage,
    addPage,
    clearPagedData: () => pagingInitialState,
  },
});

/**
 * A function that can be supplied to reducer function of an array.
 * @example
 *  Sample : let data = [{id=1,name="abc"}, {id=2,name="xyz"}]
 *  Usage : data.reduce(byIdReducerFn('id'), {})
 *  Output : {
 *    1: {
 *     id: 1,
 *     name: "abc"
 *    },
 *    2: {
 *     id: 2,
 *     name: "xyz"
 *    }
 *  }
 * @param {string} idFieldName Id field of an object in array.
 * @public
 * @returns An object indexed by id field as key.
 */
export const byIdReducerFn = (idFieldName) => (previous, current) => {
  const newVal = { ...previous };
  newVal[current[idFieldName]] = current;
  return newVal;
};

/**
 * @public
 * @description Default store state for an entity.
 */
export const defaultInitialState = {
  byId: {},
};
